
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/auth";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PremiumHeader from "@/components/premium/PremiumHeader";
import PlanTabs from "@/components/premium/PlanTabs";
import GrowthStatisticsCard from "@/components/premium/GrowthStatisticsCard";
import ConnectivityError from "@/components/premium/ConnectivityError";
import ErrorDetails from "@/components/premium/ErrorDetails";
import { useSubscription } from "@/components/premium/hooks/useSubscription";

const Premium = () => {
  const location = useLocation();
  const { user, refreshPremiumStatus } = useAuth();
  const { 
    isLoading,
    errorDetails,
    connectivityError,
    isTestingConnection,
    handleSubscribe,
    testEdgeFunctionConnectivity,
    checkEdgeFunctionStatus,
    clearErrorDetails
  } = useSubscription();

  // Added effect to force refresh premium status on page load
  useEffect(() => {
    if (user) {
      console.log("Premium page loaded, refreshing premium status");
      refreshPremiumStatus();
    }
  }, [user, refreshPremiumStatus]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const success = searchParams.get('success');
    const canceled = searchParams.get('canceled');
    
    if (success === 'true') {
      toast.success("Successfully subscribed to premium plan!");
      
      // Refresh premium status multiple times to ensure it's updated
      // This is a workaround for potential race conditions with the webhook
      const refreshAttempts = async () => {
        if (user) {
          // First immediate refresh
          await refreshPremiumStatus();
          
          // Second refresh after 2 seconds
          setTimeout(async () => {
            await refreshPremiumStatus();
            
            // Third refresh after another 3 seconds
            setTimeout(async () => {
              await refreshPremiumStatus();
            }, 3000);
          }, 2000);
        }
      };
      
      refreshAttempts();
    } else if (canceled === 'true') {
      toast.error("Payment was canceled");
    }
  }, [location.search, refreshPremiumStatus, user]);

  useEffect(() => {
    const state = location.state as { action?: string } | undefined;
    if (state?.action === 'subscribe') {
      handleSubscribe();
    }
  }, [location.state, handleSubscribe]);

  return (
    <main className="min-h-screen pt-24 pb-16">
      <Header />
      
      <div className="container max-w-5xl mx-auto px-4">
        <PremiumHeader />

        {connectivityError && (
          <ConnectivityError
            onTestConnection={testEdgeFunctionConnectivity}
            onDiagnostics={checkEdgeFunctionStatus}
            isTestingConnection={isTestingConnection}
          />
        )}

        {errorDetails && (
          <ErrorDetails
            errorDetails={errorDetails}
            onDismiss={clearErrorDetails}
          />
        )}

        <PlanTabs />
        
        <GrowthStatisticsCard />
      </div>
      
      <Footer />
    </main>
  );
};

export default Premium;
